import { useMemo, useState } from "react";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/base/MovieCard";
import EmptyState from "@/components/base/EmptyState";
import { movies } from "@/mocks/movies";
import { useSeo } from "@/hooks/useSeo";

const genreOptions = ["全部", "动作", "爱情", "科幻", "剧情", "悬疑", "历史", "运动", "惊悚", "奇幻", "犯罪", "西部", "都市", "励志", "家庭", "冒险"];
const regionOptions = ["全部", "国内", "欧美", "日韩"];
const yearOptions = ["全部", "2025", "2024", "2023"];
const sortOptions = [
  { key: "rating", label: "评分最高" },
  { key: "year", label: "最新上映" },
] as const;

const PAGE_STEP = 8;

type SortKey = (typeof sortOptions)[number]["key"];

export default function CategoryRecommend() {
  useSeo({
    title: "精选影片推荐 - 91电影",
    description:
      "91电影精选影片推荐栏目，涵盖动作、爱情、科幻、悬疑等多种类型的优质影片，支持按类型、地区、年份筛选与评分排序，帮你快速找到心仪影片。",
    keywords: "影片推荐,精选影片,电影推荐,高分电影,91电影",
  });

  const [genre, setGenre] = useState("全部");
  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState("全部");
  const [sort, setSort] = useState<SortKey>("rating");
  const [visible, setVisible] = useState(PAGE_STEP);

  const filtered = useMemo(() => {
    const list = movies.filter((m) => {
      if (genre !== "全部" && !m.genres.includes(genre)) return false;
      if (region !== "全部" && m.region !== region) return false;
      if (year !== "全部" && String(m.year) !== year) return false;
      return true;
    });
    return list.sort((a, b) =>
      sort === "rating" ? b.rating - a.rating : b.year - a.year,
    );
  }, [genre, region, year, sort]);

  const shown = filtered.slice(0, visible);

  const resetFilters = () => {
    setGenre("全部");
    setRegion("全部");
    setYear("全部");
    setSort("rating");
    setVisible(PAGE_STEP);
  };

  const FilterRow = ({
    label,
    icon,
    options,
    value,
    onChange,
    activeClass,
  }: {
    label: string;
    icon: string;
    options: string[];
    value: string;
    onChange: (v: string) => void;
    activeClass: string;
  }) => (
    <div className="flex flex-col md:flex-row md:items-start gap-3 md:gap-6">
      <span className="flex items-center gap-2 text-sm font-medium text-foreground-700 shrink-0 md:pt-1.5">
        <i className={`${icon} text-foreground-500`} />
        {label}
      </span>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => {
              onChange(opt);
              setVisible(PAGE_STEP);
            }}
            className={`px-3.5 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
              value === opt
                ? activeClass
                : "bg-background-100 text-foreground-600 hover:bg-background-200"
            }`}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="w-full px-4 md:px-8 py-8 animate-fade-up">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "影片推荐" }]} />

      <div className="mt-5 mb-8">
        <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">
          影片推荐
        </h1>
        <p className="text-sm md:text-base text-foreground-500 mt-2 max-w-2xl">
          基于口碑、评分与观影热度综合筛选，只推荐真正值得一看的影片。支持按类型、地区、年份多维筛选。
        </p>
      </div>

      <div className="bg-background-50 rounded-lg border border-background-200 p-4 md:p-5 mb-8 flex flex-col gap-4">
        <FilterRow
          label="类型"
          icon="ri-film-line"
          options={genreOptions}
          value={genre}
          onChange={setGenre}
          activeClass="bg-primary-500 text-background-50 font-medium"
        />
        <div className="h-px bg-background-200" />
        <FilterRow
          label="地区"
          icon="ri-earth-line"
          options={regionOptions}
          value={region}
          onChange={setRegion}
          activeClass="bg-primary-500 text-background-50 font-medium"
        />
        <div className="h-px bg-background-200" />
        <FilterRow
          label="年份"
          icon="ri-calendar-line"
          options={yearOptions}
          value={year}
          onChange={setYear}
          activeClass="bg-primary-500 text-background-50 font-medium"
        />
        <div className="h-px bg-background-200" />
        <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
          <span className="flex items-center gap-2 text-sm font-medium text-foreground-700 shrink-0">
            <i className="ri-sort-desc text-foreground-500" />
            排序
          </span>
          <div className="flex flex-wrap gap-2">
            {sortOptions.map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => {
                  setSort(opt.key);
                  setVisible(PAGE_STEP);
                }}
                className={`px-3.5 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  sort === opt.key
                    ? "bg-accent-500 text-secondary-950 font-bold"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-foreground-500">
          共找到 <span className="font-bold text-primary-700">{filtered.length}</span> 部影片
        </p>
        {(genre !== "全部" || region !== "全部" || year !== "全部") && (
          <button
            type="button"
            onClick={resetFilters}
            className="flex items-center gap-1 text-sm text-foreground-500 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line" />
            重置筛选
          </button>
        )}
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          icon="ri-filter-off-line"
          title="没有找到符合条件的影片"
          description="换个类型、地区或年份试试，或者重置筛选浏览全部影片。"
          action={
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium rounded-md px-5 py-2.5 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-refresh-line" />
              重置筛选
            </button>
          }
        />
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
            {shown.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
          {visible < filtered.length && (
            <div className="flex justify-center mt-10">
              <button
                type="button"
                onClick={() => setVisible((v) => v + PAGE_STEP)}
                className="inline-flex items-center gap-2 border border-primary-300 bg-background-50 hover:bg-primary-50 text-primary-700 text-sm font-medium rounded-md px-6 py-3 transition-colors whitespace-nowrap cursor-pointer"
              >
                加载更多
                <i className="ri-arrow-down-line" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}